import { useEffect, useRef } from 'react';
import { pricingPlans } from '../../../mocks/landingData';
import PricingComparisonTable from './PricingComparisonTable';

export default function PricingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" ref={sectionRef} className="relative py-20 md:py-28 bg-midnight">
      <div className="absolute inset-0 bg-gradient-to-b from-midnight via-royal-navy/10 to-midnight" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-champagne-gold/3 blur-3xl" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="animate-on-scroll opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-champagne-gold/30 bg-champagne-gold/10 mb-6">
              <i className="ri-price-tag-3-line text-champagne-gold text-sm" />
              <span className="text-champagne-gold text-sm font-medium">Pricing</span>
            </div>

            <h2 className="animate-on-scroll opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-warm-white mb-6">
              Start free.{' '}
              <span className="gold-gradient-text">Upgrade when you want unlimited coaching.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`animate-on-scroll opacity-0 relative rounded-2xl p-6 md:p-8 border transition-all duration-300 card-hover ${
                  plan.highlighted
                    ? 'bg-royal-navy/30 border-champagne-gold/40'
                    : 'bg-midnight border-royal-navy/30 hover:border-champagne-gold/30'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full gold-gradient text-midnight text-xs font-bold">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-xl font-semibold text-warm-white mb-2">{plan.name}</h3>
                  <p className="text-silver-grey text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-warm-white">${plan.price}</span>
                  <span className="text-silver-grey text-sm">/{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <i className="ri-check-line text-champagne-gold mt-0.5 flex-shrink-0" />
                      <span className="text-silver-grey text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://tnt-coach-ai.base44.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center py-3 px-6 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
                    plan.highlighted
                      ? 'gold-gradient text-midnight hover:opacity-90'
                      : 'border border-warm-white/30 text-warm-white hover:bg-warm-white/10'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          <PricingComparisonTable />
        </div>
      </div>
    </section>
  );
}